"use client";

import dynamic from "next/dynamic";
import React, { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "@/components/Link";
import { Show, UserButton } from "@clerk/nextjs";
import AuthSignInButton from "@/components/AuthSignInButton";
import { IconBolt } from "@tabler/icons-react";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import FavoriteButton from "@/components/FavoriteButton";
import Logo from "@/components/Logo";
import NavbarSearch from "@/components/NavBarSearch";

const NotificationBell = dynamic(() => import("@/components/NotificationBell"));
const TokenBolt = dynamic(() => import("@/components/TokenBolt"));

import { navLinks } from "@/lib/shared/constants";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
import { useAuthContext } from "@/context/authContext";
import MobileMenu from "./MobileMenu";
import SocialMenu from "./SocialMenu";
import InfoMenu from "./InfoMenu";
import AnalyticsMenu from "./AnalyticsMenu";

import styles from "./styles.module.scss";
import { Box as MantineBox } from "@mantine/core";

const RenderLink = (
  to: string,
  label: string,
  pathname: string,
  featured?: boolean | string,
  title?: string
) => {
  return (
    <Link
      prefetch={false}
      key={to}
      href={to}
      data-active={pathname === to}
      data-featured={featured}
      title={title}
    >
      {label}
    </Link>
  );
};

const renderLinks = (pathname: string, isHomePage: boolean) => (
  <>
    {navLinks
      .filter(({ url }) => {
        if (url === "/search") {
          return isHomePage;
        }
        return true;
      })
      .map(({ name, url, featured }) => RenderLink(url, name, pathname, featured))}
  </>
);

const Navbar = () => {
  const pathname = usePathname();
  const router = useRouter();
  const { isTester, isProUser } = useAuthContext();
  const tokensRemaining = useTokenBudgetStore((state) => state.tokensRemaining);
  const tokensLimit = useTokenBudgetStore((state) => state.tokensLimit);
  const tokenInitialized = useTokenBudgetStore((state) => state.initialized);
  const isSearchPage = pathname.includes("/search") && !pathname.includes("/search/product/");
  const isHomePage = pathname === "/";
  const [hasScrolledPastHero, setHasScrolledPastHero] = useState(false);
  const [openMenu, setOpenMenu] = useState<"analytics" | "info" | "social" | null>(null);

  useEffect(() => {
    if (!isHomePage) return;

    const handleScroll = () => {
      // Adjust this value based on your hero section height
      const heroHeight = typeof window !== "undefined" ? window.innerHeight * 0.4 : 800;
      setHasScrolledPastHero(window.scrollY > heroHeight);
    };

    handleScroll(); // Check initial position
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  const shouldShowSearch = !isSearchPage;
  const isSearchVisible = !isHomePage || hasScrolledPastHero;

  return (
    <>
      <nav data-static={isHomePage} className={`${styles.navBar} ${styles.desktopNav}`}>
        <MantineBox className={styles.logoSection}>
          <Link href="/" className={styles.logo}>
            <Logo />
          </Link>
        </MantineBox>

        <MantineBox className={styles.right}>
          {shouldShowSearch && (
            <MantineBox className={styles.searchSection} data-visible={String(isSearchVisible)}>
              <NavbarSearch />
            </MantineBox>
          )}
          <MantineBox className={styles.linksContainer}>
            {renderLinks(pathname, isHomePage)}
            {(isTester || isProUser) &&
              RenderLink("/appraise", "APPRAISE", pathname, true, "Item appraisal")}
            <InfoMenu
              isOpen={openMenu === "info"}
              onToggle={() => setOpenMenu(openMenu === "info" ? null : "info")}
            />
            <AnalyticsMenu
              isOpen={openMenu === "analytics"}
              onToggle={() => setOpenMenu(openMenu === "analytics" ? null : "analytics")}
            />
            <SocialMenu
              isOpen={openMenu === "social"}
              onToggle={() => setOpenMenu(openMenu === "social" ? null : "social")}
            />
            <Show when="signed-out">
              <AuthSignInButton />
              <FavoriteButton showCount={false} forceShow />
            </Show>
            <Show when="signed-in">
              <MantineBox style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                {TOKEN_UI_ENABLED && <TokenBolt />}
                <NotificationBell />
                <FavoriteButton showCount={false} forceShow />
              </MantineBox>
              <UserButton userProfileUrl="/profile" userProfileMode="navigation">
                <UserButton.MenuItems>
                  {TOKEN_UI_ENABLED && (
                    <UserButton.Action
                      label={
                        tokenInitialized
                          ? `${tokensRemaining} / ${tokensLimit} tokens today`
                          : "Daily tokens"
                      }
                      labelIcon={<IconBolt size={16} />}
                      onClick={() => router.push("/profile/billing")}
                    />
                  )}
                </UserButton.MenuItems>
              </UserButton>
            </Show>
          </MantineBox>
        </MantineBox>
      </nav>
      <MobileMenu />
    </>
  );
};

export default Navbar;
